import { Modifier, SelectionState } from 'draft-js';
import memoize from 'memoize-one';
import { Component } from 'react';

import { clamp } from 'my-utils';

import TableCell from './TableCell';
import {
  ADD_BUTTON_OFFSET,
  AddButton,
  ColumnHandle,
  HANDLE_CLASSNAME,
  HANDLE_OFFSET,
  RowHandle,
  TableOptionsHandle,
} from './TableHandles';
import {
  deleteColumn,
  deleteRow,
  getAdjacentCell,
  getColumnCount,
  insertColumn,
  insertRow,
  isHeaderCell,
  MIN_COLUMN_WIDTH,
  normalizeTable,
  setCellContent,
  setColumnWidth,
} from './tableUtils';

import ClickAwayListener from '@mui/material/ClickAwayListener';

export default class TableBlock extends Component {
  state = { activeCell: null, focusPosition: 'end', menuOpen: false, nestedEditorFocus: false, resize: null };

  getTable = memoize(normalizeTable);

  componentWillUnmount() {
    this.removeResizeListeners();
  }

  getTableData() {
    const { block, contentState } = this.props;
    return this.getTable(contentState.getEntity(block.getEntityAt(0)).getData());
  }

  isFocused() {
    const { block, blockProps } = this.props;
    return block.getKey() === blockProps.focusedBlockKey;
  }

  // `contentState.mergeEntityData` mutates the shared entity store and hands back the very same
  // ContentState, so `EditorState.push` upstream is a no-op and nothing re-renders on its own. The
  // change still propagates and persists (Editor passes forcePropagate for entity updates); we just
  // have to redraw this subtree ourselves whenever the change is structural.
  updateTable(updater, rerender = true) {
    const { block, blockProps, contentState } = this.props;
    const entityKey = block.getEntityAt(0);
    const table = this.getTableData();
    const nextTable = updater(table);
    if (nextTable === table) return;

    blockProps.onUpdateContentState(contentState.mergeEntityData(entityKey, nextTable));
    if (rerender) this.forceUpdate();
  }

  activateCell(rowIndex, columnIndex, focusPosition = 'end') {
    const { block, blockProps } = this.props;
    this.setState({ activeCell: { columnIndex, rowIndex }, focusPosition });
    if (!this.isFocused()) blockProps.onFocus('EDITOR', block.getKey());
  }

  handleActivateCell = (rowIndex, columnIndex) => this.activateCell(rowIndex, columnIndex);

  handleToggleNestedEditorFocus = nestedEditorFocus => this.setState({ nestedEditorFocus });
  handleToggleMenu = menuOpen => this.setState({ menuOpen });

  handleClickAway = e => {
    if (e.defaultPrevented) return;
    this.setState({ activeCell: null });
    this.props.blockProps.onBlur();
  };

  handleCellChange = (rowIndex, columnIndex, content) =>
    this.updateTable(table => setCellContent(table, rowIndex, columnIndex, content), false);

  handleNavigate = (rowIndex, columnIndex, direction, canAppendRow) => {
    const table = this.getTableData();
    const target = getAdjacentCell(table, rowIndex, columnIndex, direction);

    if (target) {
      const toEnd = direction === 'previous' || direction === 'up';
      this.activateCell(target.rowIndex, target.columnIndex, toEnd ? 'end' : 'start');
    } else if (direction === 'next' && canAppendRow) {
      // Tabbing out of the final cell grows the table and lands in the new row, matching Notion.
      this.updateTable(current => insertRow(current, current.rows.length));
      this.activateCell(table.rows.length, 0, 'start');
    }
  };

  handleInsertRow = index => {
    this.updateTable(table => insertRow(table, index));
    this.activateCell(index, this.state.activeCell?.columnIndex || 0, 'start');
  };
  handleInsertColumn = index => {
    this.updateTable(table => insertColumn(table, index));
    this.activateCell(this.state.activeCell?.rowIndex || 0, index, 'start');
  };
  handleDeleteRow = index => {
    this.setState({ activeCell: null });
    this.updateTable(table => deleteRow(table, index));
  };
  handleDeleteColumn = index => {
    this.setState({ activeCell: null });
    this.updateTable(table => deleteColumn(table, index));
  };

  handleToggleHeaderRow = () => this.updateTable(table => ({ ...table, headerRow: !table.headerRow }));
  handleToggleHeaderColumn = () => this.updateTable(table => ({ ...table, headerColumn: !table.headerColumn }));

  handleDeleteTable = () => {
    const { block, blockProps, contentState } = this.props;
    const blockKey = block.getKey();
    const beforeBlock = contentState.getBlockBefore(blockKey);

    let updatedContentState;
    if (beforeBlock) {
      updatedContentState = Modifier.removeRange(
        contentState,
        SelectionState.createEmpty().merge({
          anchorKey: beforeBlock.getKey(),
          anchorOffset: beforeBlock.getLength(),
          focusKey: blockKey,
          focusOffset: block.getLength(),
        }),
        'backward',
      );
    } else {
      // Nothing above to merge into, so leave an empty paragraph in the table's place.
      updatedContentState = Modifier.removeRange(
        contentState,
        SelectionState.createEmpty(blockKey).merge({ anchorOffset: 0, focusOffset: block.getLength() }),
        'backward',
      );
      updatedContentState = Modifier.setBlockType(
        updatedContentState,
        SelectionState.createEmpty(blockKey),
        'unstyled',
      );
    }

    this.setState({ activeCell: null });
    blockProps.onBlur();
    blockProps.onUpdateContentState(updatedContentState);
  };

  handleStartResize = (e, columnIndex) => {
    e.preventDefault();
    const cellEl = e.currentTarget.closest('th, td');
    const tableEl = cellEl?.closest('table');
    if (!tableEl) return;

    // Cap the drag so every other column can still hold its minimum, otherwise the fixed layout
    // squeezes them to nothing once the pinned width approaches the table width.
    const otherColumns = getColumnCount(this.getTableData()) - 1;
    const maxWidth = Math.max(tableEl.clientWidth - MIN_COLUMN_WIDTH * otherColumns, MIN_COLUMN_WIDTH);
    const startWidth = cellEl.getBoundingClientRect().width;

    window.addEventListener('mousemove', this.handleResizeMove);
    window.addEventListener('mouseup', this.endResize);
    this.setState({ resize: { columnIndex, maxWidth, startWidth, startX: e.clientX, width: startWidth } });
  };

  handleResizeMove = e => {
    this.setState(({ resize }) => {
      if (!resize) return null;
      const width = clamp(resize.startWidth + e.clientX - resize.startX, MIN_COLUMN_WIDTH, resize.maxWidth);
      return { resize: { ...resize, width } };
    });
  };

  endResize = () => {
    this.removeResizeListeners();
    const { resize } = this.state;
    this.setState({ resize: null });
    if (resize) this.updateTable(table => setColumnWidth(table, resize.columnIndex, resize.width));
  };

  removeResizeListeners() {
    window.removeEventListener('mousemove', this.handleResizeMove);
    window.removeEventListener('mouseup', this.endResize);
  }

  render() {
    const { blockProps } = this.props;
    const { menuOpen, nestedEditorFocus } = this.state;
    const { readOnly } = blockProps;
    const focused = this.isFocused();

    // Mirrors the Callout wrapper: a non-contentEditable island inside a contentEditable host, which
    // stops the 'select' event propagating and triggering Draft's getUpdatedSelectionState error.
    const table = (
      <div contentEditable={!readOnly} css={rootCss} onSelect={stopPropagation} suppressContentEditableWarning>
        <div contentEditable={false} css={readOnly ? readOnlyContainerCss : containerCss}>
          {this.renderTable()}
        </div>
      </div>
    );

    if (readOnly) return table;

    return (
      <ClickAwayListener
        mouseEvent={focused && !nestedEditorFocus && !menuOpen && 'onMouseDown'}
        onClickAway={this.handleClickAway}
        touchEvent={focused && !nestedEditorFocus && !menuOpen && 'onTouchEnd'}
      >
        {table}
      </ClickAwayListener>
    );
  }

  renderTable() {
    const { blockProps } = this.props;
    const { activeCell, focusPosition, resize } = this.state;
    const { onToggleSolutionKey, onUpdateToolbarProps, readOnly, reportLatexErrors, showSolutions } = blockProps;

    const table = this.getTableData();
    const columnCount = getColumnCount(table);
    const focused = this.isFocused();

    return (
      <div css={frameCss}>
        <table css={tableCss}>
          <colgroup>
            {table.columnWidths.map((width, columnIndex) => {
              const liveWidth = resize?.columnIndex === columnIndex ? resize.width : width;
              return <col key={columnIndex} style={liveWidth ? { width: liveWidth } : undefined} />;
            })}
          </colgroup>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={row.key}>
                {row.cells.map((cell, columnIndex) => {
                  const CellTag = isHeaderCell(table, rowIndex, columnIndex) ? 'th' : 'td';
                  return (
                    <CellTag key={cell.key}>
                      {!readOnly && rowIndex === 0 && (
                        <ColumnHandle
                          canDelete={columnCount > 1}
                          columnIndex={columnIndex}
                          onDelete={this.handleDeleteColumn}
                          onInsert={this.handleInsertColumn}
                          onStartResize={this.handleStartResize}
                          onToggleMenu={this.handleToggleMenu}
                        />
                      )}
                      {!readOnly && columnIndex === 0 && (
                        <RowHandle
                          canDelete={table.rows.length > 1}
                          onDelete={this.handleDeleteRow}
                          onInsert={this.handleInsertRow}
                          onToggleMenu={this.handleToggleMenu}
                          rowIndex={rowIndex}
                        />
                      )}
                      <TableCell
                        active={focused && activeCell?.rowIndex === rowIndex && activeCell?.columnIndex === columnIndex}
                        columnIndex={columnIndex}
                        content={cell.content}
                        focusPosition={focusPosition}
                        onActivate={this.handleActivateCell}
                        onChange={this.handleCellChange}
                        onNavigate={this.handleNavigate}
                        onToggleNestedEditorFocus={this.handleToggleNestedEditorFocus}
                        onToggleSolutionKey={onToggleSolutionKey}
                        onUpdateToolbarProps={onUpdateToolbarProps}
                        readOnly={readOnly}
                        reportLatexErrors={reportLatexErrors}
                        rowIndex={rowIndex}
                        showSolutions={showSolutions}
                      />
                    </CellTag>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {!readOnly && (
          <>
            <TableOptionsHandle
              headerColumn={Boolean(table.headerColumn)}
              headerRow={Boolean(table.headerRow)}
              onDeleteTable={this.handleDeleteTable}
              onToggleHeaderColumn={this.handleToggleHeaderColumn}
              onToggleHeaderRow={this.handleToggleHeaderRow}
              onToggleMenu={this.handleToggleMenu}
            />
            <AddButton onAdd={() => this.handleInsertColumn(columnCount)} orientation="column" tooltip="add column" />
            <AddButton onAdd={() => this.handleInsertRow(table.rows.length)} orientation="row" tooltip="add row" />
          </>
        )}
      </div>
    );
  }
}

function stopPropagation(e) {
  e.stopPropagation();
}

const rootCss = { '&:focus': { outline: 'none' } };

const readOnlyContainerCss = ({ spacing }) => ({ margin: spacing(1, 0), overflowX: 'auto' });

const containerCss = theme => ({
  ...readOnlyContainerCss(theme),
  // Room for the hover handles, which hang outside the table on the top and left edges and for the
  // trailing add buttons on the right and bottom. The scroll container would clip them otherwise.
  paddingTop: HANDLE_OFFSET,
  paddingLeft: HANDLE_OFFSET,
  paddingRight: ADD_BUTTON_OFFSET,
  paddingBottom: ADD_BUTTON_OFFSET,

  [`&:hover .${HANDLE_CLASSNAME}`]: { opacity: 1 },
});

const frameCss = { position: 'relative' };

const tableCss = ({ palette, spacing }) => {
  const headerBorderColor = palette.mode === 'dark' ? palette.grey[600] : palette.grey[400];
  return {
    width: '100%',
    margin: 0,
    tableLayout: 'fixed',
    borderCollapse: 'collapse',

    '& th, & td': {
      position: 'relative', // containing block for the row and column handles
      padding: spacing(0.5, 1),
      border: `1px solid ${palette.divider}`,
      verticalAlign: 'top',
      textAlign: 'left',
      fontWeight: 'inherit',
      wordBreak: 'break-word',
    },
    '& th': {
      backgroundColor: palette.mode === 'dark' ? palette.grey[800] : palette.grey[100],
      borderColor: headerBorderColor,
    },
  };
};
